import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import Radio, { RadioProps } from '@mui/material/Radio'

import { FormControl, FormHelperText, RadioGroup } from '@mui/material'
import { first, isEmpty } from 'lodash'
import { useContext } from 'react'
import { ChangeSourceEnum, FormField, updateExperimentFormField } from '../../../api'
import { AppStateContext } from '../../../state/AppProvider'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { FormFieldLabel } from '../FormFieldLabel'
import styles from './BinaryRadioFormField.module.css'

interface Props {
  name: string
  gridSize: number
  data: FormField<boolean>
  stepName: string
  fieldName: string
  convId: string
  disabled?: boolean
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const BinaryRadioFormField = ({
  name,
  gridSize,
  data,
  stepName,
  fieldName,
  convId,
  disabled = false,
  onError,
}: Props) => {
  const appStateContext = useContext(AppStateContext)

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    try {
      const newValue = event.target.value == 'true'
      const newConversation = await updateExperimentFormField(
        stepName,
        fieldName,
        newValue,
        convId,
        appStateContext?.state.shouldValidateForm ?? false,
        appStateContext?.state.frontendSettings || undefined,
      )

      if (newConversation) {
        const messages = appStateContext?.state.currentChat?.messages || []
        newConversation.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
      }
    } catch (error) {
      const errorWithReactElement = error as Error & { reactElement?: React.ReactNode }
      onError?.({
        title: 'Error',
        description:
          errorWithReactElement?.reactElement ||
          (error instanceof Error ? error.message : 'An error occurred while updating the field'),
        type: 'error',
      })
    }
  }

  const CustomRadio = (props: RadioProps) => (
    <Radio
      {...props}
      className={isEmpty(data.errors) ? (disabled ? styles.disabledRadio : styles.radio) : styles.errorRadio}
    />
  )

  return (
    <Grid
      size={gridSize}
      id={`field_${fieldName}`}>
      <FormFieldLabel
        text={name}
        isAiGenerated={data?.change_source !== ChangeSourceEnum.MANUAL}
      />

      <FormControl
        error={!isEmpty(data.errors)}
        fullWidth
        disabled={disabled}>
        <RadioGroup
          row
          onChange={handleChange}
          value={data.field_value ?? ''}>
          <FormControlLabel
            control={<CustomRadio />}
            label="Yes"
            value={true}
            classes={{ label: isEmpty(data.errors) ? undefined : styles.errorLabel }}
          />
          <FormControlLabel
            control={<CustomRadio />}
            label="No"
            value={false}
            classes={{ label: isEmpty(data.errors) ? undefined : styles.errorLabel }}
          />
        </RadioGroup>
        <FormHelperText>{first(data.errors)}</FormHelperText>
      </FormControl>
    </Grid>
  )
}
